import { Box, Typography } from '@material-ui/core'
import { ReactComponent as BackArrowCircle } from 'icons/back-arrow-circle.svg'
import { AdminContainer } from '../AdminContainer'
import { ReportedContentButton } from './styled'
import { ChangeEvent, FC, useEffect, useState } from 'react'
import handleLongText from 'common/utils/handleLongText'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useAdminReportedList } from 'pages/administration/hooks/reported-list/useAdminReportedList'
import { Loader } from 'components/core/loader'
import { ConfirmationDialog } from 'components/core/modal'
import styled from 'styled-components'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { ReportActivity, ReportedContentAction, ReportedContentStatus, ReportedUserActionType, ReportedUserType } from 'common/types'
import { useNotification } from 'hooks/use-notification/useNotification'
import { htmlToReactParser } from 'common/HTMLToReactParser'
import { AnalyticUserType, useAnalytics } from 'hooks/use-analytics/useAnalytics'
import ReportedDetailsMenu from './reported-user-management/components/ReportedDetailsMenu'
import { ReportModal } from './reported-user-management/components/ReportModal'
import { useContent } from 'pages/content/hooks/useContent'
import { observer } from 'mobx-react'
import { ReportedContentState } from 'models/reported-content/reported-content.store'
import ReportedLogs from './ReportedLogs'
import ReportActivityLogs from './ReportActivityLogs'
import { createJobSlug } from 'common/utils/createJobSlug'

type ReportContentRow = {
  title: string
  value: string
}

const StyledReportMessage = styled(Typography)`
  && {
    font-size: 1.25rem;
    color: ${({ theme }) => theme.colors.accent1};
    margin-top: 1rem;
    font-weight: 400;
  }
`

const StyledContentTypeText = styled(Box)`
  &&::first-letter {
    text-transform: capitalize;
  }
`

const ReportContentRow: FC<ReportContentRow> = ({ title, value }) => {
  return (
    <Box display={'flex'} alignItems="center" width="100%" mb={1.5}>
      <Box fontWeight="bolder" alignSelf={'flex-start'} minWidth="9.5rem">
        {title} :
      </Box>
      <StyledContentTypeText title={value}>{handleLongText({ text: value, count: 200 })}</StyledContentTypeText>
    </Box>
  )
}

const StyledContentDetailsContainer = styled(Box)`
  && {
    margin: 8.125rem 5.125rem 0.75rem 5.125rem;
    color: ${({ theme }) => theme.colors.accent1};
  }
`

const StyledReportedHeader = styled(Box)`
  && {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2.813rem;
  }
`

const StyledReportedHeading = styled(Typography)`
  && {
    font-weight: 500;
    font-size: 2rem;
    line-height: 1.25rem;
    color: ${({ theme }) => theme.colors.accent1};
    padding-bottom: 0.75rem;
  }
`

const StyledReportedDetailsWrapper = styled(Box)`
  && {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
`

const StyledLeftWrapper = styled(Box)`
  && {
    align-items: center;
  }
`
const StyledRightWrapper = styled(Box)`
  && {
    background-color: ${({ theme }) => theme.colors.supplementary};
    border-radius: 1rem;
    padding: 26px 25px;
  }
`

const StyledViewMoreButton = styled(Box)`
  && {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.125rem;
    cursor: pointer;
    margin-bottom: 21px;
  }
`

const StyledReportedTitle = styled(Typography)`
  && {
    font-weight: 500;
    font-size: 18px;
    line-height: 23px;
    margin-bottom: 0.25rem;
  }
`

const StyledReportedReason = styled(Typography)`
  && {
    font-weight: 400;
    font-size: 1rem;
    line-height: 22px;
  }
`

const StyledContentWrapper = styled(Typography)`
  && {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`

const StyledNoContentBox = styled(Box)`
  && {
    text-align: center;
  }
`

function ReportedContentDetails() {
  const history = useHistory()
  const { reportLogPage, reportActivityLogs, reportActivityPage, setReportActivityPage, isReady, reportedContent, setReportedContent, isLoading, numberOfDays, setNumberOfDays, showCloseReportModal, setShowCloseReportModal, showSuspendUser, setShowSuspendUser, showBlockUser, setShowBlockUser, reason, setReason, isAssignedToMyself, setShouldDisableAllActions, handleCloseReportModal, showReportContentActionModal, setShowReportContentActionModal} = ReportedContentState
  
  const {
    getReportDetails,
    reportedContentAction,
    deleteReportedContent,
    getReportActivityLogs,
    getReportLogs,
  } = useAdminReportedList()
  const { getContentById } = useContent()
  const { reportId } = useParams<{ reportId: string }>()
  const { backendUser, backendPartner } = useAuth()
  const { notify } = useNotification()
  const { handleTracker } = useAnalytics()
  const handleOnchange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if ((e?.target?.value).includes('.')) return
    if (parseInt(e?.target?.value) < 1) {
      setNumberOfDays(1)
    } else {
      setNumberOfDays(parseInt(e?.target?.value))
    }
  }


  const handleDeleteReportedContent = async () => {
    try {
      await deleteReportedContent(backendUser?.id, reportedContent?.contentId)
      notify('success', `Report Successfully deleted `)
      setShowReportContentActionModal(false)
      history.replace('/admin/reported-content')
    } catch (error) {
      notify('error', `Content not found`)
      setShowReportContentActionModal(false)
    }
  }

  const handleViewReportedContent = async (e: any) => {
    try {
      const response = await getContentById(reportedContent?.contentId)
      reportedContent?.type === 'post' &&
        window.open(`/feed/post/${reportedContent?.contentId}`, '_blank')
      reportedContent?.type === 'comment' &&
        window.open(
          `/feed/post/${reportedContent?.content?.parentContentId}?contentReported=${reportedContent?.contentId}`,
          '_blank'
        )

      reportedContent?.type === 'job' &&
        window.open(`/job/${createJobSlug(reportedContent?.jobId, reportedContent?.contentTitle)}`, '_blank')
    } catch (error) {
      setReportedContent(null)
    }
  }

  const handleReasonChange = event => {
    setReason(event.target.value)
  }
  
  const handleReportAction = async (actionType: ReportedContentStatus | ReportedUserActionType, eventName?: string) => {
    try {
      const payload: ReportActivity = {
        reportedContentId: reportedContent?.contentId,
        reportedUserId: reportedContent?.contentOwnerId,
        action: actionType,
        actionDuration: numberOfDays?.toString(),
        feedback: reason,
      }
      await reportedContentAction(backendUser?.id, payload)
      const logData = {
        'User ID': backendUser?.id,
        'Account Type': backendUser?.partnerId ? 'Partner' : backendUser?.role,
        'Partner ID': backendPartner?.id,
        'Company Name': backendPartner?.name
      }
      logData[`${actionType} User ID`] = reportedContent?.reporteeId

      await handleTracker(AnalyticUserType.ALL, eventName, logData, 'mixpanel', null, backendUser)
      notify('success', actionType === ReportedContentStatus.CLOSED_REPORT ? `Report closed successfully` : `User has been ${actionType}`)
      setReportedContent({
        ...reportedContent,
        status: actionType === ReportedContentStatus.CLOSED_REPORT && ReportedContentStatus.CLOSED_REPORT
      })
      setShowSuspendUser(false)
      setShowBlockUser(false)
      setReason('')
      setNumberOfDays(null)
      setShowReportContentActionModal(false)
      setShouldDisableAllActions(true)
      setShowCloseReportModal(false)
      return
    } catch (error) {
      notify('warning', actionType === ReportedContentStatus.CLOSED_REPORT ? `This reported has already been closed!` : `User has already been ${actionType}`)
      setReason('')
      setNumberOfDays(null)
      setShowBlockUser(false)
      setShowReportContentActionModal(false)
      setShowSuspendUser(false)
      setShowCloseReportModal(false)
      return
    }
  }
 
  const activityLogPageSize = 3;
  const reportLogPageSize = 10;

  useEffect(() => {
    getReportLogs(reportLogPage, reportLogPageSize, reportedContent?.contentId)
    getReportActivityLogs(reportActivityPage, activityLogPageSize, reportedContent?.contentId)
  }, [])
  

  return (
    <AdminContainer title={''} stickTopBar>
      {!reportedContent ? (
        <Loader />
      ) : (
        <>
          <StyledContentDetailsContainer>
            <StyledReportedHeader>
              <ReportedContentButton onClick={() => history.goBack()}>
                <BackArrowCircle />
              </ReportedContentButton>
              <StyledReportedHeading>Report Content Details</StyledReportedHeading>
              <ReportedDetailsMenu />
            </StyledReportedHeader>

            <StyledReportedDetailsWrapper>
              <StyledLeftWrapper>
                <ReportContentRow
                  title="Content Owner"
                  value={handleLongText({
                    text: `${reportedContent ? reportedContent?.contentOwner : ''} 
                   `,
                    count: 50
                  })}
                />
                <ReportContentRow title="Content Type" value={reportedContent ? reportedContent?.type : ''} />
              </StyledLeftWrapper>

              <StyledRightWrapper>
                {!reportedContent ? (
                  <StyledNoContentBox>Content No Longer Exists.</StyledNoContentBox>
                ) : (
                  <>
                    <Box marginBottom="1rem">
                      <StyledReportedTitle>
                        <Box
                          dangerouslySetInnerHTML={{
                            __html: `${handleLongText({ text: `${reportedContent?.contentRef}`, count: 60 })}`
                          }}
                        ></Box>
                      </StyledReportedTitle>
                      <StyledReportedReason>
                        <Box>
                          <StyledContentWrapper>
                            {reportedContent?.type === 'post' && reportedContent?.content?.content && (
                              htmlToReactParser.parse(
                                JSON.parse((reportedContent?.content?.content as unknown) as string)?.body
                              )
                            )}

                            {reportedContent?.type !== 'post' && reportedContent?.content?.content?.[0]?.comment && (
                              htmlToReactParser.parse(
                                reportedContent?.content?.content[0]?.comment
                              )
                            )}
                          </StyledContentWrapper>
                        </Box>
                      </StyledReportedReason>
                    </Box>
                    <StyledViewMoreButton
                      onClick={e => {
                        handleViewReportedContent(e)
                      }}
                    >
                      <Typography>View More</Typography>
                    </StyledViewMoreButton>
                  </>
                )}
              </StyledRightWrapper>
            </StyledReportedDetailsWrapper>
          <ReportedLogs />  
          <ReportActivityLogs />
          </StyledContentDetailsContainer>
        </>
      )}

      <ConfirmationDialog
        maxWidth="xs"
        message={<StyledReportMessage>{'Are you sure want to delete the reported content'}</StyledReportMessage>}
        onClose={() => setShowReportContentActionModal(false)}
        open={showReportContentActionModal}
        onConfirm={async () => await handleDeleteReportedContent()}
        okBtnLabel="OK"
        cancelBtnLabel="CANCEL"
      ></ConfirmationDialog>

      <ReportModal
        onClose={() => handleCloseReportModal()}
        show={(showSuspendUser ? showSuspendUser : showBlockUser) || showCloseReportModal}
        title={
          (showCloseReportModal && ReportedContentAction.CLOSE_REPORT) ||
          (showSuspendUser && ReportedContentAction.SUSPEND_USER) ||
          (showBlockUser && ReportedContentAction.BLOCK_USER)
        }
        textareaInputProps={{
          name:
            (showCloseReportModal && 'close-report') ||
            (showSuspendUser && 'suspend-user') ||
            (showBlockUser && 'block-user'),
          placeholder:
            (showCloseReportModal && 'Type your reason to close the content....') ||
            (showSuspendUser && 'It was found that this content went against the OBsidi rules') ||
            (showBlockUser && 'Type your reason to block the user'),
          value: reason,
          onChange: handleReasonChange
        }}
        actionInfoText={
          (showCloseReportModal &&
            'By clicking Close, the report will be marked closed and no further action can be taken') ||
          (showSuspendUser &&
            `By clicking suspend this user will be suspended for ${
              numberOfDays >= 1 ? numberOfDays : `<number of days entered>`
            } days.`) ||
          (showBlockUser && 'By clicking block this user will be definitely blocked from the Obsidi.')
        }
        buttonProps={{
          disabled: reason.length <= 0 || (showSuspendUser && isNaN(numberOfDays)) || !isAssignedToMyself,
          label:
            (showCloseReportModal && ReportedUserType.CLOSE) ||
            (showSuspendUser && ReportedUserType.SUSPEND) ||
            (showBlockUser && ReportedUserType.BLOCK),
          onClick: () => {
            showSuspendUser && handleReportAction(ReportedUserActionType.SUSPEND, numberOfDays.toString())
            showBlockUser && handleReportAction(ReportedUserActionType.BLOCK)
            showCloseReportModal && handleReportAction(ReportedContentStatus.CLOSED_REPORT)
          }
        }}
        handleOnchange={handleOnchange}
        numberOfSuspendDays={numberOfDays}
      />
    </AdminContainer>
  )
}

export default observer(ReportedContentDetails)
